import { BuyerListItem } from "@/types/buyerSearch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Clock,
  Calendar,
  Pencil,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";

interface BuyerDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buyerItem: BuyerListItem | null;
  onEdit: (item: BuyerListItem) => void;
  onRemove: (buyerId: string) => void;
}

export const BuyerDetailModal = ({
  open,
  onOpenChange,
  buyerItem,
  onEdit,
  onRemove,
}: BuyerDetailModalProps) => {
  if (!buyerItem) return null;

  const buyer = buyerItem.buyer_profile;
  const isPrivate = buyerItem.buyer_type === 'private';

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Buyer Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header with avatar and basic info */}
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={buyer?.profile_image_url} alt={buyer?.full_name} />
              <AvatarFallback className="text-lg">
                {getInitials(buyer?.full_name || 'N/A')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-xl font-semibold">{buyer?.full_name || 'N/A'}</h3>
              {buyer?.company_name && (
                <div className="flex items-center gap-2 text-muted-foreground mt-1">
                  <Building2 className="h-4 w-4" />
                  <span>{buyer.company_name}</span>
                </div>
              )}
              <Badge variant={isPrivate ? "secondary" : "default"} className="mt-2">
                {isPrivate ? "Private Buyer" : "Platform Buyer"}
              </Badge>
            </div>
          </div>

          <Separator />

          {/* Contact Information */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase">Contact Information</h4>
            {buyer?.email && (
              <a
                href={`mailto:${buyer.email}`}
                className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
              >
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>{buyer.email}</span>
              </a>
            )}
            {buyer?.phone_number && (
              <a
                href={`tel:${buyer.phone_number}`}
                className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
              >
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{buyer.phone_number}</span>
              </a>
            )}
          </div>

          <Separator />

          {/* Investment Profile */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase">Investment Profile</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Deals Annually</p>
                  <p className="font-medium">{buyer?.deals_annually || 'N/A'}</p>
                </div>
              </div>
              {buyer?.average_close_time && (
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Average Close Time</p>
                    <p className="font-medium">{buyer.average_close_time}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Purchasing Types */}
          {buyer?.purchasing_type && Array.isArray(buyer.purchasing_type) && buyer.purchasing_type.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase">Purchasing Types</h4>
                <div className="flex flex-wrap gap-2">
                  {buyer.purchasing_type.map((type: string) => (
                    <Badge key={type} variant="outline">
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Preferred Markets */}
          {buyer?.preferred_markets && Array.isArray(buyer.preferred_markets) && buyer.preferred_markets.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase">Preferred Markets</h4>
                <div className="flex flex-wrap gap-2">
                  {buyer.preferred_markets.map((market: string) => (
                    <Badge key={market} variant="secondary">
                      <MapPin className="h-3 w-3 mr-1" />
                      {market}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Notes */}
          {buyerItem.notes && (
            <>
              <Separator />
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase">Notes</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{buyerItem.notes}</p>
              </div>
            </>
          )}

          {/* Added Date */}
          <Separator />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>
              Added {buyerItem.added_at ? format(new Date(buyerItem.added_at), 'MMM d, yyyy') : 'N/A'}
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            {isPrivate && (
              <Button
                onClick={() => {
                  onEdit(buyerItem);
                  onOpenChange(false);
                }}
                className="flex-1"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit Buyer
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => {
                onRemove(buyerItem.buyer_id);
                onOpenChange(false);
              }}
              className="flex-1"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove from List
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
